import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp, Sel } from "./Enquiries";
import { Plus, UserCircle, Trash, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";

const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function Doctors() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/doctors")).data);
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="doctors-page">
      <PageHeader title="Doctors" subtitle={`${items.length} doctor${items.length===1?"":"s"} on the roster`}
        actions={
          <button onClick={()=>setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
            data-testid="add-doctor-btn">
            <Plus size={16} weight="bold"/> Add doctor
          </button>
        }/>
      <div className="px-6 sm:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((d) => (
            <div key={d.id} className="bg-white border border-zinc-200 rounded-sm p-5"
              data-testid={`doctor-card-${d.id}`}>
              <div className="flex items-start gap-3">
                <div className="rounded-sm w-12 h-12 flex items-center justify-center text-white font-black text-lg"
                     style={{background: d.color || "#E60000", fontFamily:"Manrope, sans-serif"}}>
                  {(d.name || "?").split(" ").slice(-1)[0]?.[0] || "D"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-zinc-900">{d.name}</div>
                  <div className="text-xs text-zinc-500">{d.specialization || "â€”"}</div>
                  {d.has_login && <div className="text-[10px] uppercase tracking-[0.25em] text-green-700 font-bold mt-1">â˜… Has Login</div>}
                </div>
                <button onClick={()=>setEditing(d)} className="text-zinc-500 hover:text-[#E60000]" data-testid={`edit-doctor-${d.id}`}>
                  <Pencil size={16} weight="bold"/>
                </button>
              </div>
              <div className="mt-3 text-xs text-zinc-600">{d.phone || d.email || "â€”"}</div>
              <div className="mt-3 flex flex-wrap gap-1">
                {WEEKDAYS.map((wd, idx) => {
                  const has = (d.working_hours || []).find(h => h.weekday === idx);
                  return (
                    <span key={idx} className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${has?"bg-zinc-900 text-white":"bg-zinc-100 text-zinc-400"}`}>
                      {wd}
                    </span>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Slot: {d.slot_duration_min} min
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-zinc-500 col-span-full text-center py-8">No doctors yet.</div>}
        </div>
      </div>

      {showNew && <DoctorModal onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false); load();}}/>}
      {editing && <DoctorModal item={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null); load();}}/>}
    </div>
  );
}

function DoctorModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || "",
    specialization: item?.specialization || "",
    phone: item?.phone || "",
    email: item?.email || "",
    bio: item?.bio || "",
    color: item?.color || "#E60000",
    slot_duration_min: item?.slot_duration_min || 30,
    has_login: item?.has_login || false,
    password: "",
  });
  const defaultHours = WEEKDAYS.map((_, idx) => {
    const ex = (item?.working_hours || []).find(h => h.weekday === idx);
    return ex ? { weekday: idx, start: ex.start, end: ex.end, enabled: true } :
                { weekday: idx, start: "10:00", end: "18:00", enabled: false };
  });
  const [hours, setHours] = useState(defaultHours);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!window.confirm("Remove this doctor?")) return;
    await api.delete(`/doctors/${item.id}`);
    toast.success("Removed");
    onSaved();
  };

  const save = async () => {
    setBusy(true);
    try {
      const working_hours = hours.filter(h => h.enabled).map(h => ({ weekday: h.weekday, start: h.start, end: h.end }));
      const payload = { ...form, slot_duration_min: Number(form.slot_duration_min), working_hours };
      // Strip empty optional string fields (Pydantic EmailStr rejects "")
      ["email", "phone", "specialization", "bio", "password"].forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      if (!isEdit && form.has_login && (!form.email || !form.password)) {
        toast.error("Email and password required for login-enabled doctor");
        setBusy(false);
        return;
      }
      if (isEdit) {
        const { has_login, password, ...rest } = payload;
        await api.patch(`/doctors/${item.id}`, rest);
      } else {
        await api.post("/doctors", payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit doctor" : "Add doctor"}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Inp label="Full name" v={form.name} onChange={(v)=>setForm({...form,name:v})} testid="d-name"/>
        <Inp label="Specialization" v={form.specialization} onChange={(v)=>setForm({...form,specialization:v})} testid="d-spec"/>
        <Inp label="Phone" v={form.phone} onChange={(v)=>setForm({...form,phone:v})} testid="d-phone"/>
        <Inp label="Email" v={form.email} onChange={(v)=>setForm({...form,email:v})} testid="d-email"/>
        <Inp label="Color" type="color" v={form.color} onChange={(v)=>setForm({...form,color:v})} testid="d-color"/>
        <Sel label="Slot duration (min)" v={String(form.slot_duration_min)} onChange={(v)=>setForm({...form,slot_duration_min:v})}
             opts={[["15","15"],["20","20"],["30","30"],["45","45"],["60","60"]]} testid="d-slot"/>
      </div>
      <div className="mt-3">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Bio</label>
        <textarea rows={2} value={form.bio} onChange={(e)=>setForm({...form,bio:e.target.value})}
          className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900" data-testid="d-bio"/>
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 mb-2">Working hours</div>
        <div className="space-y-1">
          {hours.map((h, i) => (
            <div key={i} className="flex items-center gap-2">
              <label className="flex items-center gap-1 w-14 text-xs font-bold uppercase tracking-wider">
                <input type="checkbox" checked={h.enabled} onChange={(e)=>setHours(s=>s.map((x,xi)=> xi===i?{...x,enabled:e.target.checked}:x))} data-testid={`d-wd-${i}`}/>
                {WEEKDAYS[i]}
              </label>
              <input type="time" value={h.start} disabled={!h.enabled} onChange={(e)=>setHours(s=>s.map((x,xi)=>xi===i?{...x,start:e.target.value}:x))}
                className="border border-zinc-300 rounded-sm px-2 py-1 text-sm disabled:bg-zinc-50 focus:outline-none focus:border-zinc-900"/>
              <span className="text-xs text-zinc-500">to</span>
              <input type="time" value={h.end} disabled={!h.enabled} onChange={(e)=>setHours(s=>s.map((x,xi)=>xi===i?{...x,end:e.target.value}:x))}
                className="border border-zinc-300 rounded-sm px-2 py-1 text-sm disabled:bg-zinc-50 focus:outline-none focus:border-zinc-900"/>
            </div>
          ))}
        </div>
      </div>

      {!isEdit && (
        <div className="mt-5 border-t border-zinc-200 pt-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.has_login} onChange={(e)=>setForm({...form, has_login: e.target.checked})} data-testid="d-haslogin"/>
            <span className="font-bold uppercase tracking-wider text-xs">Enable doctor login</span>
          </label>
          {form.has_login && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs text-zinc-500">Doctor will log in with the email above and this password. They'll see only patients assigned to them.</div>
              <Inp label="Password" type="password" v={form.password} onChange={(v)=>setForm({...form,password:v})} testid="d-pass"/>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between">
        {isEdit ? (
          <button onClick={remove} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1" data-testid="d-delete">
            <Trash size={14} weight="bold"/> Remove
          </button>
        ) : <span/>}
        <div className="flex gap-2">
          <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="d-save">
            {busy ? "Savingâ€¦" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
